import {
    Box,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Spinner,
    Badge,
    Icon,
    Tooltip,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import DeviceApi from 'api/device';
import Card from 'components/card/Card';
import { MdSmartphone, MdHistory, MdCheckCircle, MdCancel, MdInfo } from 'react-icons/md';
import modelData from 'config/model';

export default function DeviceTable(props) {
    const { userId } = props;
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = 'gray.400';
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Memoized Map for efficient lookup from 300k+ models
    const deviceMap = React.useMemo(() => {
        const map = new Map();
        if (Array.isArray(modelData)) {
            modelData.forEach(m => {
                if (m && m.Device) {
                    map.set(String(m.Device).toLowerCase(), m);
                }
            });
        }
        return map;
    }, []);

    const fetchList = async () => {
        try {
            setLoading(true);
            const response = await DeviceApi.List(userId);
            if (response.data.status_code === 200) {
                setList(response.data.data);
                setIsError(false);
            } else {
                setIsError(true);
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
            setIsError(true);
            console.log(err);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchList();
        }
    }, [userId]);

    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
            boxShadow={cardShadow}
            bg='white'
            borderRadius='20px'
            p='20px'
            mt='20px'
        >
            <Flex align='center' mb='20px' px='10px'>
                <Icon as={MdSmartphone} w='24px' h='24px' color={textColor} me='10px' />
                <Text color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    Device Activity Log
                </Text>
            </Flex>
            <Box overflowX='auto'>
                <Table variant='simple' color='gray.500'>
                    <Thead>
                        <Tr>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>DEVICE / ID</Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>PLATFORM / VERSION</Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>APP VERSION</Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>LAST LOGIN</Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>LAST SEEN</Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize='xs' color={textColorSecondary}>STATUS</Text>
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {loading ? (
                            <Tr>
                                <Td colSpan={6} textAlign='center' py='40px'>
                                    <Spinner size='lg' color='brand.500' />
                                    <Text mt='10px' color={textColorSecondary}>Fetching device records...</Text>
                                </Td>
                            </Tr>
                        ) : isError ? (
                            <Tr>
                                <Td colSpan={6} textAlign='center' py='40px'>
                                    <Icon as={MdCancel} w='40px' h='40px' color='red.400' />
                                    <Text mt='10px' color='red.400'>Failed to load data. Please check connection.</Text>
                                </Td>
                            </Tr>
                        ) : list?.length > 0 ? (
                            list.map((row, index) => {
                                let details = row.details;
                                if (typeof details === 'string') {
                                    try {
                                        details = JSON.parse(details);
                                    } catch (e) { }
                                }

                                // Parse x-device-info if available
                                let deviceInfo = {};
                                let others = details?.others || {};

                                if (others['x-device-info']) {
                                    try {
                                        deviceInfo = JSON.parse(others['x-device-info']);
                                    } catch (e) { }
                                }

                                // Enhanced Platform Detection
                                let platform = 'Unknown';
                                if (others['sec-ch-ua-platform']) {
                                    platform = others['sec-ch-ua-platform'].replace(/\"/g, '');
                                } else if (others['x-platform']) {
                                    platform = others['x-platform'];
                                } else if (deviceInfo.isAndroid === 'true') {
                                    platform = 'android';
                                } else if (deviceInfo.deviceName === 'iPhone' || deviceInfo.deviceName === 'iPad') {
                                    platform = 'ios';
                                } else if (others['user-agent']?.includes('Mozilla')) {
                                    platform = 'Web Browser';
                                }

                                // Clean up model name
                                const rawModel = deviceInfo.deviceName || row.device_name || '';
                                let modelName = rawModel;

                                // Efficient lookup in deviceMap (O(1))
                                const mappedModel = deviceMap.get(String(rawModel).toLowerCase());

                                if (mappedModel) {
                                    modelName = `${mappedModel['Retail Branding']} ${mappedModel['Marketing Name']}`.trim();
                                }

                                if (!modelName && others['user-agent']?.includes('Macintosh')) {
                                    modelName = 'MacBook / iMac';
                                } else if (!modelName) {
                                    modelName = 'Generic Device';
                                }

                                const model = modelName;
                                // Clean up version string
                                let version = deviceInfo.version || '';
                                if (version.includes('/')) {
                                    const match = version.match(/:(\d+)\//);
                                    if (match) version = match[1];
                                }
                                version = version ? `v${version}` : '';

                                const appVersion = others['x-app-version-name'] ? `${others['x-app-version-name']} (${others['x-app-version-code']})` : 'NA'

                                const displayDetails = typeof details === 'object' ?
                                    Object.entries(details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ') :
                                    details;

                                const lastSeen = row.last_active;
                                const lastLogin = row.last_login;

                                return (
                                    <Tr key={index} _hover={{ bg: 'gray.50' }} transition='all 0.2s'>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Flex direction='column'>
                                                <Text color={textColor} fontWeight='700'>
                                                    {model}
                                                </Text>
                                                <Flex gap='5px' mt='2px'>
                                                    <Badge size='xs' variant='outline' colorScheme='orange' fontSize='10px'>
                                                        {others['cf-ipcountry'] || 'IN'}
                                                    </Badge>
                                                    <Text color={textColorSecondary} fontSize='xs' isTruncated maxW='150px'>
                                                        {others['cf-connecting-ip'] || others['x-real-ip'] || row.device_id}
                                                    </Text>
                                                </Flex>
                                            </Flex>
                                        </Td>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Flex align='center'>
                                                <Badge
                                                    variant='outline'
                                                    colorScheme={platform.toLowerCase() === 'ios' ? 'blue' : platform.toLowerCase() === 'android' ? 'green' : 'gray'}
                                                    mr='10px'
                                                    textTransform='uppercase'
                                                >
                                                    {platform}
                                                </Badge>
                                                <Text color={textColor} fontWeight='400'>
                                                    {version}
                                                </Text>
                                                <Tooltip label={displayDetails} hasArrow placement='top' textAlign='left'>
                                                    <Box as='span' ml='10px' cursor='pointer'>
                                                        <Icon as={MdInfo} color='brand.400' />
                                                    </Box>
                                                </Tooltip>
                                            </Flex>
                                        </Td>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Text color={textColor} fontWeight='500'>
                                                {appVersion !== 'N/A' ? `v${appVersion}` : appVersion}
                                            </Text>
                                        </Td>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Flex align='center'>
                                                <Icon as={MdHistory} mr='5px' color={textColorSecondary} />
                                                <Text color={textColor} fontWeight='400'>
                                                    {lastSeen ? moment(lastLogin).format('DD MMM YYYY, HH:mm') : 'N/A'}
                                                </Text>
                                            </Flex>
                                        </Td>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Flex align='center'>
                                                <Icon as={MdHistory} mr='5px' color={textColorSecondary} />
                                                <Text color={textColor} fontWeight='400'>
                                                    {lastSeen ? moment(lastSeen).format('DD MMM YYYY, HH:mm') : 'N/A'}
                                                </Text>
                                            </Flex>
                                        </Td>
                                        <Td fontSize='sm' borderColor={borderColor}>
                                            <Badge
                                                px='10px'
                                                py='4px'
                                                borderRadius='8px'
                                                fontSize='xs'
                                                variant='subtle'
                                                colorScheme={row.is_active ? 'green' : 'gray'}
                                            >
                                                <Flex align='center'>
                                                    <Icon as={row.is_active ? MdCheckCircle : MdCancel} mr='5px' />
                                                    {row.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                </Flex>
                                            </Badge>
                                        </Td>
                                    </Tr>
                                );
                            })
                        ) : (
                            <Tr>
                                <Td colSpan={4} textAlign='center' py='40px'>
                                    <Text color={textColorSecondary} fontStyle='italic'>No device records found for this user.</Text>
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>
        </Card>
    );
}
